import { EnvironmentProviders, Provider, inject, isDevMode, provideAppInitializer } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { SwRegistrationOptions, SwUpdate, provideServiceWorker } from '@angular/service-worker';
import { NativeBridgeService, SHELL_USER_AGENT } from '../webview/native-bridge.service';

/** WebView Android : `; wv)` dans l'agent, y compris dans un binaire qui ne se nomme pas encore. */
const ANDROID_WEBVIEW = /; wv\)/;

/**
 * Le service worker ne tourne que dans un navigateur, jamais dans la coque.
 *
 * - iOS : WKWebView n'accepte un service worker qu'avec les App-Bound Domains,
 *   qui bornent la navigation à 10 domaines et casseraient la connexion Google
 *   et Apple faite dans la WebView (ARC-06).
 * - Android : la WebView l'accepterait, mais un service worker y survit aux
 *   mises à jour du binaire et ne se vide qu'en effaçant les données de l'app.
 *   Un état abîmé y resterait sans recours, et l'écran natif « Pas de
 *   connexion » ne se montrerait plus sur Android seulement.
 *
 * Dans la coque, le hors ligne passe par cet écran natif au lancement, puis
 * par l'instantané du `DataStore` (NA-05).
 */
export function serviceWorkerAllowed(isApp: boolean, userAgent: string, devMode: boolean): boolean {
  return !devMode && !isApp && !ANDROID_WEBVIEW.test(userAgent);
}

/**
 * Enveloppe de l'app gardée par le service worker Angular (`ngsw-config.json`) :
 * index, scripts, styles, traductions et images, pour ouvrir l'app sans réseau
 * dans un navigateur. L'API n'y passe jamais : ses réponses hors ligne sont
 * l'instantané du `DataStore`, propre au compte connecté.
 *
 * `index.html` vient du réseau dès qu'il répond (`navigationRequestStrategy:
 * freshness`) : jamais une ancienne version après un déploiement ; la copie
 * gardée ne sert que réseau coupé.
 */
export function provideAppShellCache(): (Provider | EnvironmentProviders)[] {
  return [
    // `app-sw.js` charge celui d'Angular et laisse passer l'API sans y toucher (voir le fichier).
    provideServiceWorker('app-sw.js'),
    {
      provide: SwRegistrationOptions,
      useFactory: () => ({
        enabled: serviceWorkerAllowed(inject(NativeBridgeService).isApp, inject(SHELL_USER_AGENT), isDevMode()),
        // Après le premier écran : l'installation ne dispute pas le réseau au démarrage.
        registrationStrategy: 'registerWhenStable:30000',
      }),
    },
    provideAppInitializer(() => {
      const updates = inject(SwUpdate);
      // Fichiers gardés effacés par le navigateur : la version en cours ne peut
      // plus se charger, on repart du réseau.
      if (updates.isEnabled) updates.unrecoverable.subscribe(() => document.location.reload());
      else {
        refreshEntryCache();
        reloadOnNewVersion(inject(DOCUMENT));
      }
    }),
  ];
}

/**
 * Avant le 27/10/2026, `index.html` partait sans Cache-Control : la WebView
 * d'iOS le gardait alors des jours sans le redemander (validité estimée à 10 %
 * de son âge, une dizaine de jours pour la version de juillet), et chaque
 * lancement rouvrait l'ancienne app. Une fois la nouvelle version chargée (par
 * le lien d'une notification, qui n'est pas en cache), on remplace l'entrée
 * de la coque par la réponse actuelle, servie en no-cache : les lancements
 * suivants redemandent la page. À retirer après le 15/12/2026.
 */
export function refreshEntryCache(): void {
  for (const path of ['/', '/index.html']) {
    fetch(path, { cache: 'reload' }).catch(() => undefined);
  }
}

/** Absence au-delà de laquelle le retour au premier plan cherche une nouvelle version. */
export const NEW_VERSION_CHECK_AFTER_MS = 5 * 60 * 1000;

/** Script principal d'un `index.html` (`main-XXXX.js`), `null` sans empreinte (`ng serve`, tests). */
export function mainScriptOf(html: string): string | null {
  return html.match(/\bmain-[A-Za-z0-9]+\.js\b/)?.[0] ?? null;
}

/**
 * Sans service worker (la coque), rien ne recharge la page : la WebView est
 * gardée d'une ouverture à l'autre et l'app resterait des jours sur la version
 * de son dernier lancement. Au retour au premier plan après une vraie absence,
 * on relit `index.html` ; un autre script principal veut dire une nouvelle
 * version en ligne : on recharge.
 *
 * Une absence courte (appareil photo, sélecteur de fichier, paiement) ne
 * recharge jamais : ce qui était en cours serait perdu.
 */
export function reloadOnNewVersion(doc: Document, now: () => number = Date.now): void {
  const running = doc.querySelector<HTMLScriptElement>('script[src^="main-"]')?.getAttribute('src') ?? null;
  if (!running) return;
  let hiddenAt: number | null = null;
  doc.addEventListener('visibilitychange', () => {
    if (doc.visibilityState === 'hidden') {
      hiddenAt = now();
      return;
    }
    const away = hiddenAt === null ? 0 : now() - hiddenAt;
    hiddenAt = null;
    if (away < NEW_VERSION_CHECK_AFTER_MS) return;
    fetch('/index.html', { cache: 'no-store' })
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        const online = mainScriptOf(html);
        if (online && online !== running) doc.location.reload();
      })
      // Hors ligne : on garde la version en cours.
      .catch(() => undefined);
  });
}
